<template>
  <page-header-wrapper>
    <div class="business-page erp-list-page baidao-list-page stock-ledger-page">
      <section class="erp-search-card">
        <a-form layout="inline">
          <a-row :gutter="24">
            <a-col :md="6" :sm="24">
              <a-form-item label="仓库">
                <a-select v-model="query.warehouseId" allow-clear placeholder="全部仓库">
                  <a-select-option v-for="item in warehouses" :key="item.id" :value="item.id">
                    {{ item.warehouseCode }} · {{ item.warehouseName }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="商品分类">
                <a-tree-select
                  v-model="query.categoryId"
                  allow-clear
                  show-search
                  tree-node-filter-prop="title"
                  :dropdown-style="{ maxHeight: '360px', overflow: 'auto' }"
                  placeholder="全部分类"
                  :tree-data="categoryTreeSelect"
                  style="width: 100%"
                />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="SPU编码">
                <a-input v-model="query.spuCode" allow-clear placeholder="SPU编码" @pressEnter="loadData(true)" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="商品名称">
                <a-input v-model="query.spuName" allow-clear placeholder="商品名称" @pressEnter="loadData(true)" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="SKU编码">
                <a-input v-model="query.skuCode" allow-clear placeholder="SKU编码" @pressEnter="loadData(true)" />
              </a-form-item>
            </a-col>
            <a-col :md="6" :sm="24">
              <a-form-item label="规格名称">
                <a-input v-model="query.specName" allow-clear placeholder="规格名称" @pressEnter="loadData(true)" />
              </a-form-item>
            </a-col>
            <a-col :md="24" :sm="24" class="erp-search-actions">
              <a-button type="primary" icon="search" @click="loadData(true)">查询</a-button>
              <a-button icon="redo" @click="resetQuery">重置</a-button>
            </a-col>
          </a-row>
        </a-form>
      </section>

      <section class="erp-table-card">
        <div class="erp-table-toolbar">
          <div>
            <h3>库存台账</h3>
            <p>数量以基本单位记账；配置了整箱单位的 SKU 会同步显示按箱规换算后的整箱数量。可用 = 实物 − 占用。</p>
          </div>
          <div class="erp-toolbar-actions">
            <span class="record-count erp-record-count">共 {{ pagination.total }} 条</span>
            <a-button icon="download" :loading="exporting" @click="exportCurrent">导出当前查询</a-button>
            <a-button v-if="canStockFlow" @click="goStockFlow">库存流水</a-button>
            <a-button @click="$router.push('/erp/stock-opening')">进入库存期初</a-button>
          </div>
        </div>
        <a-table
          :columns="columns"
          :data-source="rows"
          :loading="loading"
          row-key="id"
          bordered
          size="middle"
          :scroll="{ x: 1740 }"
          :pagination="pagination"
          :locale="{ emptyText: '暂无符合条件的库存记录' }"
          @change="onPageChange">
          <template slot="categoryName" slot-scope="text, record">
            <span class="category-path">{{ displayCategory(text, record) }}</span>
          </template>
          <template slot="qty" slot-scope="value"><span class="erp-money">{{ Number(value || 0).toFixed(3) }}</span></template>
          <template slot="availableQty" slot-scope="value"><span class="erp-money erp-available-qty">{{ Number(value || 0).toFixed(3) }}</span></template>
          <template slot="caseQty" slot-scope="value, record">
            <span v-if="record.caseUnitName && value != null" class="erp-money">{{ Number(value).toFixed(3) }} {{ record.caseUnitName }}</span>
            <span v-else>—</span>
          </template>
        </a-table>
      </section>
    </div>
  </page-header-wrapper>
</template>

<script>
import moment from 'moment'
import { listWarehouse } from '@/api/erp/warehouse'
import { exportStockBalance, pageStockBalance } from '@/api/erp/stock'
import { treeProductCategory } from '@/api/erp/productCategory'
import auth from '@/plugins/auth'

const emptyQuery = () => ({
  warehouseId: undefined,
  categoryId: undefined,
  spuCode: '',
  spuName: '',
  skuCode: '',
  specName: ''
})

export default {
  name: 'ErpStockLedger',
  data () {
    return {
      query: emptyQuery(),
      warehouses: [],
      categoryTreeRaw: [],
      rows: [],
      loading: false,
      exporting: false,
      pagination: { current: 1, pageSize: 20, total: 0, showSizeChanger: true, showTotal: total => `共 ${total} 条` },
      columns: [
        { title: '仓库', dataIndex: 'warehouseName', width: 160 },
        { title: 'SPU编码', dataIndex: 'spuCode', width: 140 },
        { title: '商品分类', dataIndex: 'categoryName', width: 200, scopedSlots: { customRender: 'categoryName' } },
        { title: '商品名称', dataIndex: 'spuName', width: 180 },
        { title: 'SKU编码', dataIndex: 'skuCode', width: 150 },
        { title: '规格名称', dataIndex: 'specName', width: 160 },
        { title: '实物数量', dataIndex: 'qtyBalance', width: 120, align: 'right', scopedSlots: { customRender: 'qty' } },
        { title: '已占用', dataIndex: 'qtyOccupied', width: 110, align: 'right', scopedSlots: { customRender: 'qty' } },
        { title: '可用数量', dataIndex: 'qtyAvailable', width: 120, align: 'right', scopedSlots: { customRender: 'availableQty' } },
        { title: '基本单位', dataIndex: 'unitName', width: 80 },
        { title: '整箱实物', dataIndex: 'qtyBalanceCase', width: 130, align: 'right', scopedSlots: { customRender: 'caseQty' } },
        { title: '整箱可用', dataIndex: 'qtyAvailableCase', width: 130, align: 'right', scopedSlots: { customRender: 'caseQty' } }
      ]
    }
  },
  computed: {
    categoryTreeSelect () {
      return this.toTreeSelectData(this.categoryTreeRaw)
    },
    canStockFlow () {
      return auth.hasPermi('erp:stock-flow:list')
    }
  },
  created () {
    listWarehouse().then(res => { this.warehouses = res.data || [] })
    this.loadCategoryTree().finally(() => this.loadData(false))
  },
  methods: {
    normalizeTree (nodes) {
      return (nodes || []).map(n => ({
        id: n.id,
        name: n.categoryName || n.name,
        categoryName: n.categoryName || n.name,
        children: this.normalizeTree(n.children || [])
      }))
    },
    toTreeSelectData (nodes) {
      return (nodes || []).map(n => ({
        title: n.categoryName || n.name,
        value: n.id,
        key: String(n.id),
        children: n.children && n.children.length ? this.toTreeSelectData(n.children) : undefined
      }))
    },
    loadCategoryTree () {
      return treeProductCategory().then(res => {
        this.categoryTreeRaw = this.normalizeTree((res && res.data) || [])
        this.refreshRowCategories()
      }).catch(() => {
        this.categoryTreeRaw = []
      })
    },
    refreshRowCategories () {
      if (!this.rows || !this.rows.length) return
      this.rows = this.rows.map(row => this.decorateBalanceRow(row))
    },
    displayCategory (text, record) {
      if (text && text !== '—') return text
      if (record && typeof record === 'object') {
        const name = record.categoryName || record.category_name
        if (name) return name
        const path = this.categoryPathText(record)
        if (path !== '—') return path
      }
      return '—'
    },
    categoryNodePath (id) {
      if (id == null || id === '') return []
      const path = []
      const walk = (nodes, acc) => {
        for (const node of nodes || []) {
          const next = acc.concat(node)
          if (String(node.id) === String(id)) {
            path.push.apply(path, next)
            return true
          }
          if (walk(node.children, next)) return true
        }
        return false
      }
      walk(this.categoryTreeRaw, [])
      return path
    },
    categoryPathText (record) {
      if (!record || typeof record !== 'object') return '—'
      const name = record.categoryName || record.category_name
      if (name) return name
      const id = record.categoryId != null ? record.categoryId : record.category_id
      if (id == null || id === '') return '—'
      const path = this.categoryNodePath(id)
      if (path.length) {
        return path.map(node => node.name || node.categoryName).join('/')
      }
      return '—'
    },
    decorateBalanceRow (row) {
      const categoryId = row.categoryId != null ? row.categoryId : row.category_id
      let categoryName = row.categoryName || row.category_name || ''
      if (!categoryName) {
        const path = this.categoryPathText({ categoryId, categoryName: '' })
        if (path !== '—') categoryName = path
      }
      return { ...row, categoryId, categoryName }
    },
    buildParams () {
      return {
        warehouseId: this.query.warehouseId,
        categoryId: this.query.categoryId,
        spuCode: (this.query.spuCode || '').trim() || undefined,
        spuName: (this.query.spuName || '').trim() || undefined,
        skuCode: (this.query.skuCode || '').trim() || undefined,
        specName: (this.query.specName || '').trim() || undefined,
        pageNum: this.pagination.current,
        pageSize: this.pagination.pageSize
      }
    },
    loadData (resetPage) {
      if (resetPage) this.pagination.current = 1
      this.loading = true
      return pageStockBalance(this.buildParams()).then(res => {
        const page = res.data || {}
        this.rows = (page.records || []).map(row => this.decorateBalanceRow(row))
        this.pagination.total = Number(page.total || 0)
      }).finally(() => { this.loading = false })
    },
    goStockFlow () {
      if (!this.canStockFlow) {
        this.$message.warning('当前账号无库存流水查看权限，请联系管理员在角色中勾选「库存流水」')
        return
      }
      const q = {}
      const textKeys = ['spuCode', 'spuName', 'skuCode', 'specName']
      textKeys.forEach(key => {
        const v = (this.query[key] || '').trim()
        if (v) q[key] = v
      })
      if (this.query.warehouseId != null) q.warehouseId = String(this.query.warehouseId)
      if (this.query.categoryId != null) q.categoryId = String(this.query.categoryId)
      this.$router.push({ path: '/erp/stock-flow', query: q })
    },
    resetQuery () {
      this.query = emptyQuery()
      this.loadData(true)
    },
    onPageChange (pager) {
      this.pagination.current = pager.current
      this.pagination.pageSize = pager.pageSize
      this.loadData(false)
    },
    async exportCurrent () {
      this.exporting = true
      try {
        const res = await exportStockBalance({
          warehouseId: this.query.warehouseId,
          categoryId: this.query.categoryId,
          spuCode: (this.query.spuCode || '').trim() || undefined,
          spuName: (this.query.spuName || '').trim() || undefined,
          skuCode: (this.query.skuCode || '').trim() || undefined,
          specName: (this.query.specName || '').trim() || undefined
        })
        const blobData = res && res.data !== undefined ? res.data : res
        if (!(blobData instanceof Blob)) {
          this.$message.error('导出失败：响应格式异常')
          return
        }
        if (blobData.type && blobData.type.includes('application/json')) {
          const text = await blobData.text()
          let msg = '当前查询无可导出数据'
          try {
            const obj = JSON.parse(text)
            msg = obj.message || obj.msg || msg
          } catch (e) { /* ignore */ }
          this.$message.warning(msg)
          return
        }
        const blob = new Blob([blobData], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `库存台账_${moment().format('YYYYMMDDHHmmss')}.xlsx`
        link.click()
        URL.revokeObjectURL(link.href)
        this.$message.success('导出成功')
      } catch (e) {
        this.$message.error((e && e.message) || '导出失败')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.record-count{color:#8b96a8;font-size:12px;white-space:nowrap}
.category-path{color:#3e4d61;white-space:normal;word-break:break-all}
</style>
